import { useContext, useEffect, useRef, useState } from "react";
import {
  MdCheckBox,
  MdCheckBoxOutlineBlank,
  MdCheckCircleOutline,
  MdOutlineCancel,
} from "react-icons/md";
import { createRecordRequest } from "services/apiRequests";
import { allowOnlyNumbers } from "utils/utils";
import { formatUpdateContact } from "utils/formatUpdate";
import { ContactListType, CreateEditContactType } from "utils/interfaces";
import { phoneRegex } from "utils/regex";
import { AnimatePresence, motion } from "framer-motion";
import { LoadingDots } from "utils/loadingAnimation";
import { MainContext } from "components/utils/Contexts";
import { defaultBgVariants, defaultPanelVariants } from "./defaultValues";
import PopUpHeader from "./PopUpHeader";

interface CreateEditUserProps {
  userToEdit?: ContactListType | null;
  usersListToCheck?: ContactListType[];
  confirmButtonText: string;
  showPrimaryOption?: boolean;
  handlePressBack: () => void;
  handleSaveFunction: (
    data: CreateEditContactType,
    processedData: CreateEditContactType
  ) => Promise<any>;
}

const CreateEditUser: React.FC<CreateEditUserProps> = ({
  userToEdit,
  usersListToCheck,
  confirmButtonText,
  showPrimaryOption = true,
  handlePressBack,
  handleSaveFunction,
}) => {
  // States definition
  const [firstName, setFirstName] = useState("");
  const [middleName, setMiddleName] = useState("");
  const [lastName, setLastName] = useState("");
  const [email, setEmail] = useState("");
  const [phoneNumber, setPhoneNumber] = useState("");
  const [addAsPrimaryContact, setAddAsPrimaryContact] = useState(false);
  // Validation states
  const [emailValidation, setEmailValidation] = useState<
    | "waiting"
    | "not-valid"
    | "unavailable"
    | "available"
    | "error"
    | "unmodified"
  >(!!userToEdit ? "unmodified" : "waiting");
  const [usernameValidation, setUsernameValidation] = useState<
    | "waiting"
    | "not-valid"
    | "unavailable"
    | "available"
    | "error"
    | "unmodified"
  >(!!userToEdit ? "unmodified" : "waiting");
  const [phoneValidation, setPhoneValidation] = useState<
    "waiting" | "not-valid" | "valid" | "unmodified"
  >(!!userToEdit ? "unmodified" : "waiting");
  const [isSaving, setIsSaving] = useState(false);

  // Ref to access the email input element
  const emailInputRef = useRef<HTMLInputElement>(null);

  // Get the context values
  const { contextSchoolByRole } = useContext(MainContext);

  /**
   * Function used to activate or deactivate create user button
   */
  const activateCreateButtonFunction = () => {
    // If there's something to edit.
    // If we are editing, check that the values have changed.
    // If we are creating, then just rely on the checking values.
    const createEditActivation = !!userToEdit
      ? firstName !== userToEdit.firstName ||
        middleName !== userToEdit.middleName ||
        lastName !== userToEdit.lastName ||
        phoneNumber !== userToEdit.phone ||
        email !== userToEdit.email
      : true;

    // Check the nature of each state
    // 1. Check mandatory fields
    // 2. Check appropriate validation
    const checkStates = firstName &&
    lastName &&
    email &&
    phoneNumber &&
    ["available", "unmodified"].includes(emailValidation) &&
    ["available", "unmodified"].includes(usernameValidation) &&
    ["valid", "unmodified"].includes(phoneValidation)

    return createEditActivation && checkStates;
  };

  // Variable that controls the activation of the create button 
  const activateCreateButton = activateCreateButtonFunction();

  /**
   * If there is selected data, then prefill the defined fields
   */
  useEffect(() => {
    if (userToEdit) {
      setFirstName(userToEdit.firstName);
      setMiddleName(userToEdit.middleName ? userToEdit.middleName : "");
      setLastName(userToEdit.lastName);
      setEmail(userToEdit.email);
      setPhoneNumber(userToEdit.phone);
    }
  }, []);

  /**
   * Function used to validate the current email
   */
  const handleEmailCheck = async (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    // First of all, check if the email is defined
    if (!email) return;

    // Check if the email is in the list of users to check
    const repeatedEmail = usersListToCheck
      ? usersListToCheck.filter((user) => email === user.email).length > 0
      : false;

    // If there is a repeated email in the list, then display an error message
    if (repeatedEmail) {
      setEmailValidation("error");
      return;
    }

    // Additionally, if we are editing, and the email
    // field is the same as the selected one, then move
    // to the neutral state when editing (unmodified)
    if (userToEdit && userToEdit.email === email) {
      setEmailValidation("unmodified");
      setUsernameValidation("unmodified");
      return;
    }

    // Check if the form is valid using HTML5 checkValidity
    // Reference: https://developer.mozilla.org/en-US/docs/Web/HTML/Element/input/email#validation
    if (!(email && event.target.checkValidity())) {
      // The imail is not valid
      setEmailValidation("not-valid");
      return;
    }

    // Send the check request
    const [emailResponse, usernameResponse] = await Promise.all([
      createRecordRequest({ emailToCheck: email }, "/api/users/check-email"),
      createRecordRequest(
        { usernameToCheck: email },
        "/api/users/check-username"
      ),
    ]);

    // Check if the responses are right
    if (!emailResponse.successful) {
      // Display error message
      setEmailValidation("error");
      return;
    }
    // Check if the responses are right
    if (!usernameResponse.successful) {
      // Display error message
      setUsernameValidation("error");
      return;
    }

    // Otherwise, display the exist message
    const emailExists = emailResponse.content.exists;
    const usernameExists = usernameResponse.content.exists;

    // Set the state
    setEmailValidation(emailExists ? "unavailable" : "available");
    setUsernameValidation(usernameExists ? "unavailable" : "available");
  };

  /**
   * Function used to control the behaviour of changing the email
   * @param event
   */
  const handleChangeEmail = (event: React.ChangeEvent<HTMLInputElement>) => {
    // Force the availability state to be waiting
    setEmailValidation("waiting");
    // And set the value
    setEmail(event.target.value);
  };

  /**
   * Function used to control the behaviour of changing the phone number
   * @param event
   */
  const handleChangePhone = (event: React.ChangeEvent<HTMLInputElement>) => {
    // Force the availability state to be waiting
    setPhoneValidation("waiting");
    // Prevent from inputing letters and symbols but "+"
    setPhoneNumber(allowOnlyNumbers(event.target.value));
  };

  /**
   * Function used to validate the current phone number
   */
  const validatePhoneNumber = (phoneToValidate: string) => {
    // First of all, check if the phone is defined
    if (!phoneToValidate) return;

    // Additionally, if we are editing, and the phone
    // field is the same as the selected one, then move
    // to the neutral state (unmodified)
    if (userToEdit && userToEdit.phone === phoneToValidate) {
      setPhoneValidation("unmodified");
      return;
    }

    // Then apply the regex logic
    setPhoneValidation(
      phoneRegex.test(phoneToValidate) ? "valid" : "not-valid"
    );
  };

  /**
   * Function that returns all the form data
   * @returns data
   */
  const provideData = () => {
    // Define the provided data
    const dataToProvide: {
      firstName: string;
      middleName: string;
      lastName: string;
      email: string;
      phone: string;
      organisations?: string[];
    } = {
      firstName: firstName,
      middleName: middleName,
      lastName: lastName,
      email: email,
      phone: phoneNumber,
    };

    // Check the context if it's possible to add the organisation
    if (contextSchoolByRole && contextSchoolByRole._id) {
      dataToProvide.organisations = [contextSchoolByRole._id];
    }

    // Return all the gathered data in the form
    return [dataToProvide, formatUpdateContact(userToEdit, dataToProvide)];
  };

  /**
   * Function to handle the "Add as primary contact" checkbox
   */
  const handleAddAsPrimaryContact = () => {
    setAddAsPrimaryContact(!addAsPrimaryContact);
  };

  /**
   * Function to handle creating a contact when the button is pressed
   * @returns
   */
  const handleSaveContactWrapper = async () => {
    // Check if the button is already available
    if (!activateCreateButton) return;

    // Check if we are currently running something
    if (isSaving) return;

    // Block the function
    setIsSaving(true);

    // Get all the data from the form
    const [data, processedData] = provideData();

    // Apply the save function
    await handleSaveFunction(data, processedData);

    // Finish the saving state
    setIsSaving(false);
  };

  return (
    <>
      <motion.div
        key="create-edit-contact-blurred-background"
        className="fixed inset-0 w-full h-full bg-neutral-500 bg-opacity-40 backdrop-blur-[2px] z-20 flex flex-col flex-grow"
        variants={defaultBgVariants}
        initial="initial"
        animate="animate"
        exit="exit"
        transition={{ duration: 0.1, ease: "easeOut" }}
        onClick={handlePressBack}
      />
      <motion.div
        key="create-edit-contact"
        variants={defaultPanelVariants}
        initial="initial"
        animate="animate"
        exit="exit"
        transition={{ duration: 0.1, ease: "easeOut" }}
        className="fixed w-[462px] h-fit max-h-[90%] left-0 right-0 ml-auto mr-auto px-6 pt-6 pb-8 bottom-5 bg-white rounded-3xl flex-col justify-start items-start gap-6 inline-flex z-30"
      >
        <PopUpHeader
          key="create-edit-contact-header"
          title={!!userToEdit ? "Edit Contact" : "Add new Contact"}
          handlePressBack={handlePressBack}
        />
        <div className="w-full h-full overflow-y-auto pop-up-scrollbar flex-col justify-start items-start gap-8 inline-flex">
          <div className="self-stretch h-[280px] flex-col justify-start items-start gap-4 flex">
            <div className="self-stretch text-neutral-500 text-base font-semibold font-sans leading-[19px] select-none">
              Basic details
            </div>
            <div className="self-stretch h-fit flex-col justify-start items-start gap-1.5 flex">
              <label
                htmlFor="first-name"
                className="rounded justify-start items-start gap-2 inline-flex text-neutral-500 text-xs font-normal font-sans uppercase leading-[14px] tracking-wide select-none"
              >
                First Name*
              </label>
              <div className="self-stretch bg-neutral-50 rounded-lg justify-start items-center gap-2 inline-flex">
                <input
                  id="first-name"
                  type="text"
                  value={firstName}
                  onChange={(event) => setFirstName(event.target.value)}
                  className="w-full h-full p-3 text-neutral-700 placeholder:text-neutral-300 text-base font-sans leading-[19px] bg-transparent border-none focus:outline-none"
                  placeholder="Enter first name"
                />
              </div>
            </div>
            <div className="self-stretch h-fit flex-col justify-start items-start gap-1.5 flex">
              <label
                htmlFor="middle-name"
                className="rounded justify-start items-start gap-2 inline-flex text-neutral-500 text-xs font-normal font-sans uppercase leading-[14px] tracking-wide select-none"
              >
                Middle Name (Optional)
              </label>
              <div className="self-stretch bg-neutral-50 rounded-lg justify-start items-center gap-2 inline-flex">
                <input
                  id="middle-name"
                  type="text"
                  value={middleName}
                  onChange={(event) => setMiddleName(event.target.value)}
                  className="w-full h-full p-3 text-neutral-700 placeholder:text-neutral-300 text-base font-sans leading-[19px] bg-transparent border-none focus:outline-none"
                  placeholder="Enter middle name"
                />
              </div>
            </div>
            <div className="self-stretch h-fit flex-col justify-start items-start gap-1.5 flex">
              <label
                htmlFor="last-name"
                className="rounded justify-start items-start gap-2 inline-flex text-neutral-500 text-xs font-normal font-sans uppercase leading-[14px] tracking-wide select-none"
              >
                Last Name*
              </label>
              <div className="self-stretch bg-neutral-50 rounded-lg justify-start items-center gap-2 inline-flex">
                <input
                  id="last-name"
                  type="text"
                  value={lastName}
                  onChange={(event) => setLastName(event.target.value)}
                  className="w-full h-full p-3 text-neutral-700 placeholder:text-neutral-300 text-base font-sans leading-[19px] bg-transparent border-none focus:outline-none"
                  placeholder="Enter last name"
                />
              </div>
            </div>
          </div>
          <div className="self-stretch h-[236px] flex-col justify-start items-start gap-4 flex">
            <div className="self-stretch text-neutral-500 text-base font-semibold font-sans leading-[19px] select-none">
              Contact information
            </div>
            <div className="self-stretch h-fit flex-col justify-start items-start gap-1.5 flex">
              <label
                htmlFor="email"
                className="rounded justify-start items-start gap-2 inline-flex text-neutral-500 text-xs font-normal font-sans uppercase leading-[14px] tracking-wide select-none"
              >
                Email*
              </label>
              <div className="self-stretch h-fit inline-flex flex-col justify-start items-start">
                <div
                  className={`self-stretch bg-neutral-50 border ${
                    emailValidation === "waiting" &&
                    usernameValidation === "waiting"
                      ? "border-transparent"
                      : ["not-valid", "unavailable", "error"].includes(
                          emailValidation
                        ) ||
                        ["not-valid", "unavailable", "error"].includes(
                          usernameValidation
                        )
                      ? "border-error-500"
                      : emailValidation === "available" &&
                        usernameValidation === "available"
                      ? "border-success-500"
                      : "border-transparent"
                  } rounded-lg justify-start items-center gap-2 inline-flex relative`}
                >
                  <input
                    ref={emailInputRef}
                    id="email"
                    type="email"
                    value={email}
                    onBlur={handleEmailCheck}
                    onChange={handleChangeEmail}
                    className="w-full h-full p-3 text-neutral-700 placeholder:text-neutral-300 text-base font-sans leading-[19px] bg-transparent border-none focus:outline-none z-10"
                    placeholder="Enter email"
                  />
                  <AnimatePresence>
                    {emailValidation === "available" &&
                      usernameValidation === "available" && (
                      <motion.div
                        key="available-icon"
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        transition={{ ease: "easeInOut", duration: 0.2 }}
                        className="absolute right-2 w-6 h-6 flex justify-center items-center"
                      >
                        <MdCheckCircleOutline className="w-full h-full fill-success-500" />
                      </motion.div>
                    )}
                    {(["not-valid", "error", "unavailable"].includes(
                      emailValidation
                    ) ||
                      ["not-valid", "error", "unavailable"].includes(
                        usernameValidation
                      )) && (
                      <motion.div
                        key="unavailable-icon"
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        transition={{ ease: "easeInOut", duration: 0.2 }}
                        className="absolute right-2 w-6 h-6 flex justify-center items-center"
                      >
                        <MdOutlineCancel className="w-full h-full fill-error-500" />
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>
                <AnimatePresence mode="wait" initial={false}>
                  {/* Email error messages */}
                  {["not-valid", "error", "unavailable"].includes(
                    emailValidation
                  ) && (
                    <motion.div
                      key="email-error-message"
                      className="w-full text-error-600 text-xs font-normal font-sans tracking-tight leading-[17px] select-none"
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      exit={{ opacity: 0 }}
                    >
                      {emailValidation === "not-valid"
                        ? "The email format is incorrect"
                        : emailValidation === "error"
                        ? "Error when trying to check the email"
                        : emailValidation === "unavailable"
                        ? "The provided email is already taken"
                        : ""}
                    </motion.div>
                  )}
                  {/* Username error messages */}
                  {["not-valid", "error", "unavailable"].includes(
                    usernameValidation
                  ) && (
                    <motion.div
                      key="username-error-message"
                      className="w-full text-error-600 text-xs font-normal font-sans tracking-tight leading-[17px] select-none"
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      exit={{ opacity: 0 }}
                    >
                      {usernameValidation === "not-valid"
                        ? "The username format is incorrect"
                        : usernameValidation === "error"
                        ? "Error when trying to check the username"
                        : usernameValidation === "unavailable"
                        ? "The provided username is already taken"
                        : ""}
                    </motion.div>
                  )}
                  {/* Successful message */}
                  {emailValidation === "available" &&
                    usernameValidation === "available" && (
                    <motion.div
                      key="available"
                      className="w-full text-success-600 text-xs font-normal font-sans tracking-tight leading-[17px] select-none"
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      exit={{ opacity: 0 }}
                    >
                      The provided email is available
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
            </div>
            <div className="self-stretch h-fit flex-col justify-start items-start gap-1.5 flex relative">
              <label
                htmlFor="phone-number"
                className="rounded justify-start items-start gap-2 inline-flex text-neutral-500 text-xs font-normal font-sans uppercase leading-[14px] tracking-wide select-none"
              >
                Phone number - Mobile*
              </label>
              <div className="self-stretch h-fit inline-flex flex-col justify-start items-start">
                <div
                  className={`self-stretch bg-neutral-50 border ${
                    ["waiting", "unmodified"].includes(phoneValidation)
                      ? "border-transparent"
                      : phoneValidation === "not-valid"
                      ? "border-error-500"
                      : "border-success-500"
                  } rounded-lg justify-start items-center gap-2 inline-flex`}
                >
                  <input
                    id="phone-number"
                    type="tel"
                    value={phoneNumber}
                    onBlur={() => validatePhoneNumber(phoneNumber)}
                    onChange={handleChangePhone}
                    className="w-full h-full p-3 text-neutral-700 placeholder:text-neutral-300 text-base font-sans leading-[19px] bg-transparent border-none focus:outline-none z-10"
                    placeholder="Enter phone number"
                  />
                  <AnimatePresence>
                    {phoneValidation === "valid" && (
                      <motion.div
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        transition={{ ease: "easeInOut", duration: 0.2 }}
                        className="absolute right-2 w-6 h-6 flex justify-center items-center"
                      >
                        <MdCheckCircleOutline className="w-full h-full fill-success-500" />
                      </motion.div>
                    )}
                    {phoneValidation === "not-valid" && (
                      <motion.div
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        transition={{ ease: "easeInOut", duration: 0.2 }}
                        className="absolute right-2 w-6 h-6 flex justify-center items-center"
                      >
                        <MdOutlineCancel className="w-full h-full fill-error-500" />
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>
                <AnimatePresence mode="wait" initial={false}>
                  {phoneValidation === "not-valid" && (
                    <motion.div
                      className="w-full text-error-600 text-xs font-normal font-sans tracking-tight leading-[17px]"
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      exit={{ opacity: 0 }}
                    >
                      The phone number must follow the format: 0123456789 or
                      130000
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
            </div>
            {showPrimaryOption && (
              <div
                className="w-fit h-[27px] flex-col justify-start items-start gap-3 flex cursor-pointer"
                onClick={handleAddAsPrimaryContact}
              >
                <div className="self-stretch rounded-lg justify-start items-center gap-3 inline-flex">
                  <div className="w-6 h-6 relative">
                    {addAsPrimaryContact ? (
                      <MdCheckBox className="w-full h-full fill-neutral-300" />
                    ) : (
                      <MdCheckBoxOutlineBlank className="w-full h-full fill-neutral-300" />
                    )}
                  </div>
                  <div className="grow shrink basis-0 h-[27px] py-1 justify-start items-start gap-2 flex text-neutral-700 text-base font-normal font-sans leading-[19px] select-none">
                    Add as primary contact
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
        <button
          className={`w-full h-[46px] px-5 py-3 rounded justify-center items-center gap-2 inline-flex ${
            activateCreateButton && !isSaving
              ? "bg-secondary-600 hover:bg-secondary-700 ease-in-out duration-150 cursor-pointer"
              : "bg-Subtle cursor-not-allowed"
          }`}
          onClick={handleSaveContactWrapper}
        >
          {isSaving ? (
            <LoadingDots bgColor="bg-neutral-400" />
          ) : (
            <div
              className={
                activateCreateButton
                  ? "text-white text-lg font-semibold font-sans leading-snug select-none"
                  : "text-neutral-400 text-lg font-semibold font-sans leading-snug select-none"
              }
            >
              {confirmButtonText}
            </div>
          )}
        </button>
      </motion.div>
    </>
  );
};

export default CreateEditUser;
